import { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';

const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6'];
const OBJ_KEYS = ['human','car','bike','bus','truck'];
const OBJ_LT   = { human:'Žmonės', car:'Automobiliai', bike:'Dviračiai', bus:'Autobusai', truck:'Sunkvežimiai' };

function periodDates(type) {
  const now = new Date();
  if (type === 'week') {
    const start = new Date(now); start.setDate(now.getDate() - now.getDay() + 1); start.setHours(0,0,0,0);
    const prev  = new Date(start); prev.setDate(prev.getDate()-7);
    return { curr: { from: start, to: now }, prev: { from: prev, to: new Date(start-1) }, label: 'savaitė' };
  }
  if (type === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const prev  = new Date(now.getFullYear(), now.getMonth()-1, 1);
    const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    return { curr: { from: start, to: now }, prev: { from: prev, to: prevEnd }, label: 'mėnuo' };
  }
  if (type === 'quarter') {
    const qMonth = Math.floor(now.getMonth() / 3) * 3;
    const start  = new Date(now.getFullYear(), qMonth, 1);
    const prev   = new Date(now.getFullYear(), qMonth - 3, 1);
    const prevEnd = new Date(now.getFullYear(), qMonth, 0);
    return { curr: { from: start, to: now }, prev: { from: prev, to: prevEnd }, label: 'ketvirtis' };
  }
  if (type === 'year') {
    const start  = new Date(now.getFullYear(), 0, 1);
    const prev   = new Date(now.getFullYear() - 1, 0, 1);
    const prevEnd = new Date(now.getFullYear(), 0, 0);
    return { curr: { from: start, to: now }, prev: { from: prev, to: prevEnd }, label: 'metai' };
  }
  if (type === 'halfyear') {
    const half   = now.getMonth() < 6 ? 0 : 6;
    const start  = new Date(now.getFullYear(), half, 1);
    const prev   = new Date(now.getFullYear(), half - 6, 1);
    const prevEnd = new Date(now.getFullYear(), half, 0);
    return { curr: { from: start, to: now }, prev: { from: prev, to: prevEnd }, label: 'pusmetis' };
  }
  // day
  const today = new Date(now); today.setHours(0,0,0,0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate()-1);
  const yesterdayEnd = new Date(today-1);
  return { curr: { from: today, to: now }, prev: { from: yesterday, to: yesterdayEnd }, label: 'diena' };
}

export default function Comparison() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [period,   setPeriod]   = useState('week');
  const [clients,  setClients]  = useState([]);
  const [cameras,  setCameras]  = useState([]);
  const [selClient,setSelClient]= useState('');
  const [selCam,   setSelCam]   = useState('');
  const [data,     setData]     = useState({ curr:[], prev:[] });
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api.get('/clients').then(r => { setClients(r.data); if(r.data[0]) setSelClient(r.data[0].id); }).catch(() => {});
    } else {
      loadData();
    }
    api.get('/cameras').then(r => setCameras(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isAdmin && !selClient) return;
    if (isAdmin) loadData();
  }, [period, selClient, selCam]);

  useEffect(() => {
    if (!isAdmin) loadData();
  }, [period, selCam]);

  async function loadData() {
    setLoading(true);
    try {
      const { curr, prev } = periodDates(period);
      const fmt = d => d.toISOString().slice(0,16);
      const qCam = selCam ? `&camera=${selCam}` : '';
      const qClient = isAdmin && selClient ? `clientId=${selClient}&` : '';
      const [r1, r2] = await Promise.all([
        api.get(`/reports/data?${qClient}from=${fmt(curr.from)}&to=${fmt(curr.to)}${qCam}`),
        api.get(`/reports/data?${qClient}from=${fmt(prev.from)}&to=${fmt(prev.to)}${qCam}`),
      ]);
      setData({ curr: r1.data?.timeSeries || [], prev: r2.data?.timeSeries || [] });
    } catch(e) { setData({ curr:[], prev:[] }); }
    finally { setLoading(false); }
  }

  function sumBy(rows, key) { return (rows||[]).reduce((s,r) => s+(r[key]||0), 0); }

  const totals = OBJ_KEYS.map(k => ({
    name: OBJ_LT[k],
    Dabartinis: sumBy(data.curr, k),
    Ankstesnis: sumBy(data.prev, k),
    pokytis: sumBy(data.prev,k) ? Math.round(((sumBy(data.curr,k)-sumBy(data.prev,k))/sumBy(data.prev,k))*100) : 0,
  }));

  const { label } = periodDates(period);
  const clientCameras = isAdmin ? cameras.filter(c => !selClient || c.client_id == selClient) : cameras;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">📊 Laikotarpių palyginimas</div>
        <div className="page-subtitle">Dabartinis vs ankstesnis periodas</div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom:24 }}>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ display:'flex', gap:6 }}>
            {[['day','Šiandien vs vakar'],['week','Ši savaitė vs praėjusi'],['month','Šis mėnuo vs praėjęs'],['quarter','Šis ketvirtis vs praėjęs'],['halfyear','Šis pusmetis vs praėjęs'],['year','Šie metai vs praėję']].map(([k,v]) => (
              <button key={k} className={`btn btn-sm ${period===k?'btn-primary':'btn-secondary'}`} onClick={() => setPeriod(k)}>{v}</button>
            ))}
          </div>
          {isAdmin && (
            <select className="form-input" style={{ width:200 }} value={selClient} onChange={e => { setSelClient(e.target.value); setSelCam(''); }}>
              {clients.map(c => <option key={c.id} value={c.id}>{c.company||c.name}</option>)}
            </select>
          )}
          <select className="form-input" style={{ width:180 }} value={selCam} onChange={e => setSelCam(e.target.value)}>
            <option value="">Visos kameros</option>
            {clientCameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:24 }}>
        {totals.map((t,i) => (
          <div key={t.name} className="card" style={{ textAlign:'center', padding:'16px 12px' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:6 }}>{t.name}</div>
            <div style={{ fontSize:24, fontWeight:800, color:COLORS[i] }}>{t.Dabartinis.toLocaleString()}</div>
            <div style={{ fontSize:11, color:'var(--text-muted)' }}>vs {t.Ankstesnis.toLocaleString()}</div>
            <div style={{ fontSize:12, fontWeight:700, marginTop:4,
              color: t.pokytis > 0 ? '#10b981' : t.pokytis < 0 ? '#ef4444' : 'var(--text-muted)' }}>
              {t.pokytis > 0 ? '▲' : t.pokytis < 0 ? '▼' : '='} {Math.abs(t.pokytis)}%
            </div>
          </div>
        ))}
      </div>

      {/* Bar chart */}
      <div className="card" style={{ marginBottom:24 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:16 }}>Palyginimas pagal objekto tipą</div>
        {loading ? <div style={{ textAlign:'center', padding:60 }}><span className="loading-spinner" /></div> : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={totals} margin={{ top:5, right:30, left:0, bottom:5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill:'var(--text-muted)', fontSize:11 }} />
              <YAxis tick={{ fill:'var(--text-muted)', fontSize:11 }} />
              <Tooltip contentStyle={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:8 }} />
              <Legend />
              <Bar dataKey="Dabartinis" fill="#3b82f6" radius={[4,4,0,0]} />
              <Bar dataKey="Ankstesnis" fill="rgba(59,130,246,0.3)" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}